import { useState, type FormEvent } from 'react';
import { team } from '../../data/seedData';
import { Avatar, Badge, Card, Progress, SectionTitle } from '../../components/ui/Primitives';
import { Dialog } from '../../components/ui/Dialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useApp } from '../../lib/app/AppContext';

type Member = (typeof team)[number];

export function TeamPage() {
  const { pushToast } = useToast();
  const { invites, inviteMember, revokeInvite } = useApp();
  const [selected, setSelected] = useState<Member | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const role = data.get('role');
    if (typeof email !== 'string' || !email.trim()) return;
    if (role !== 'Viewer' && role !== 'Editor' && role !== 'Admin') return;
    const created = inviteMember(email, role);
    setInviteOpen(false);
    pushToast('Invite created', { message: created.email + ' · ' + created.role, tone: 'success' });
  }

  return (
    <div className="page-stack">
      <div className="hero-row">
        <div>
          <span className="page-kicker">Organization</span>
          <h1>Team operations</h1>
          <p>Capacity, role distribution, presence, workload and a persistent invitation queue.</p>
          {invites.length ? <p className="inline-note">{invites.length} pending invite{invites.length > 1 ? 's' : ''}</p> : null}
        </div>
        <button className="btn primary" onClick={() => setInviteOpen(true)}>Invite member</button>
      </div>

      <div className="team-grid">
        {team.map((member) => (
          <Card className="team-card" key={member.name}>
            <div className="team-top"><Avatar name={member.name} size="lg" /><Badge tone={member.status === 'Online' ? 'positive' : member.status === 'On-call' ? 'warning' : 'neutral'}>{member.status}</Badge></div>
            <h3>{member.name}</h3>
            <p>{member.role}</p>
            <small>{member.team}</small>
            <div className="workload"><span>Workload <b>{member.load}%</b></span><Progress value={member.load} /></div>
            <button className="btn secondary full" onClick={() => setSelected(member)}>View profile</button>
          </Card>
        ))}
      </div>

      <Card>
        <SectionTitle eyebrow="Access" title="Pending invitations" />
        {invites.length ? (
          <div className="invite-list">
            {invites.map((invite) => (
              <div key={invite.id}>
                <span><strong>{invite.email}</strong><small>{invite.role} · {new Date(invite.createdAt).toLocaleString()}</small></span>
                <Badge tone="warning">{invite.status}</Badge>
                <button className="btn ghost" onClick={() => { revokeInvite(invite.id); pushToast('Invite revoked', { message: invite.email, tone: 'info' }); }}>Revoke</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state compact"><strong>No pending invitations</strong><span>Create an invite to add it to the local access queue.</span></div>
        )}
      </Card>

      <Dialog open={Boolean(selected)} title={selected?.name ?? 'Team member'} onClose={() => setSelected(null)}>
        {selected ? (
          <div className="profile-detail">
            <Avatar name={selected.name} size="lg" />
            <div><strong>{selected.role}</strong><span>{selected.team}</span><span>Status: {selected.status}</span><span>Current workload: {selected.load}%</span></div>
          </div>
        ) : null}
      </Dialog>

      <Dialog open={inviteOpen} title="Create member invite" onClose={() => setInviteOpen(false)}>
        <form className="form-stack" onSubmit={invite}>
          <label>Email<input name="email" type="email" required autoComplete="email" /></label>
          <label>Role<select name="role"><option>Viewer</option><option>Editor</option><option>Admin</option></select></label>
          <button className="btn primary" type="submit">Create invite</button>
        </form>
      </Dialog>
    </div>
  );
}
